import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, RotateCw, Layers, ChevronUp, ChevronDown } from 'lucide-react';

interface VariationToggleProps {
  variations: string[];
  label: string;
  currentIndex: number;
  onIndexChange: (index: number) => void;
  isEditing?: boolean;
  autoPlay?: boolean;
  autoPlaySpeed?: number; // milliseconds
  showAllVariations?: boolean;
  onToggleShowAll?: () => void;
  maxCharLimit?: number;
}

export default function VariationToggle({
  variations,
  label,
  currentIndex,
  onIndexChange,
  isEditing = false,
  autoPlay = false,
  autoPlaySpeed = 3000,
  showAllVariations = false,
  onToggleShowAll,
  maxCharLimit
}: VariationToggleProps) {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [localIndex, setLocalIndex] = useState(currentIndex);

  // Auto-play functionality
  useEffect(() => {
    if (isPlaying && variations.length > 1) {
      const interval = setInterval(() => {
        const nextIndex = (localIndex + 1) % variations.length;
        setLocalIndex(nextIndex);
        onIndexChange(nextIndex);
      }, autoPlaySpeed);

      return () => clearInterval(interval);
    }
  }, [isPlaying, localIndex, variations.length, autoPlaySpeed, onIndexChange]);

  // Sync with external index changes
  useEffect(() => {
    setLocalIndex(currentIndex);
  }, [currentIndex]);

  const handlePrevious = () => {
    const newIndex = localIndex > 0 ? localIndex - 1 : variations.length - 1;
    setLocalIndex(newIndex);
    onIndexChange(newIndex);
    setIsPlaying(false); // Stop auto-play when manually navigating
  };

  const handleNext = () => {
    const newIndex = (localIndex + 1) % variations.length;
    setLocalIndex(newIndex);
    onIndexChange(newIndex);
    setIsPlaying(false); // Stop auto-play when manually navigating
  };

  const handleDotClick = (index: number) => {
    setLocalIndex(index);
    onIndexChange(index);
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  if (variations.length === 0) {
    return null;
  }

  const currentVariation = variations[localIndex] || '';
  const charCount = currentVariation.length;
  const charPercentage = maxCharLimit ? (charCount / maxCharLimit) * 100 : 0;

  return (
    <div className="space-y-3">
      {/* Header with controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {label} ({localIndex + 1} of {variations.length})
          </span>
        </div>

        <div className="flex items-center gap-1">
          {/* Toggle show all button */}
          {onToggleShowAll && variations.length > 1 && (
            <button
              onClick={onToggleShowAll}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={showAllVariations ? "Show single" : "Show all"}
            >
              {showAllVariations ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}

          {/* Play/Pause button */}
          {variations.length > 1 && (
            <button
              onClick={togglePlayPause}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 text-gray-600" />
              ) : (
                <Play className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}

          {/* Navigation arrows */}
          {variations.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                disabled={variations.length <= 1}
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              <button
                onClick={handleNext}
                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                disabled={variations.length <= 1}
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Current variation display or all variations */}
      {showAllVariations ? (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {variations.map((variation, index) => (
            <div
              key={index}
              className={`p-2 rounded-lg border cursor-pointer transition-all ${
                index === localIndex
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleDotClick(index)}
            >
              <div className="flex items-start justify-between">
                <span className="text-sm text-gray-700 flex-1">{variation}</span>
                <span className="text-xs text-gray-500 ml-2">
                  {variation.length}{maxCharLimit && `/${maxCharLimit}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-700">{currentVariation}</div>

          {/* Character count and progress bar */}
          {maxCharLimit && (
            <div className="mt-2 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Characters</span>
                <span className={`text-xs ${charCount > maxCharLimit ? 'text-red-600' : 'text-gray-600'}`}>
                  {charCount}/{maxCharLimit}
                </span>
              </div>
              <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    charPercentage > 100
                      ? 'bg-red-500'
                      : charPercentage > 80
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(charPercentage, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dot indicators */}
      {variations.length > 1 && !showAllVariations && (
        <div className="flex justify-center items-center gap-1">
          {variations.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`transition-all ${
                index === localIndex
                  ? 'w-6 h-1.5 bg-blue-500 rounded-full'
                  : 'w-1.5 h-1.5 bg-gray-300 rounded-full hover:bg-gray-400'
              }`}
              title={`Variation ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Quick stats */}
      {variations.length > 1 && (
        <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
          <span>Total: {variations.length} variations</span>
          {maxCharLimit && (
            <span>
              Avg length: {Math.round(variations.reduce((sum, v) => sum + v.length, 0) / variations.length)} chars
            </span>
          )}
        </div>
      )}
    </div>
  );
}